import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CandleData } from '../types/chartData'
import type { BacktestChartHandle } from '../components/BacktestChart'
import { FormingBarBuffer, type FormingBarState, type SubBarRow } from '../utils/formingBarBuffer'
import { DatafeedSocket } from '../adapters/datafeedSocket'
import {
  fetchM1SubBars,
  fetchTicks,
  subBarSourceFor,
  MAX_TICK_BAR_SECONDS,
  REALTIME_MAX_TICK_BAR_SECONDS,
  type SubBarFill,
  type SubBarSource,
} from '../adapters/subBarSocket'
import { formingBarPlan, isMarketClockDwell, type FormingBarPlan } from '../utils/formingBarPlan'
import { getPlayTiming, binarySearchFirstAfter } from './usePlayback'
import { getTimeframeSeconds } from '../../utils/chartUtils'

/**
 * Animates the bar replay is about to reveal, so the user watches it build.
 *
 * The design constraint is that replay itself must not change. `progressViewTs`
 * still advances one whole bar per playback tick, which keeps persistence,
 * scrubbing, the reveal boundary, order filtering, the equity curve, the progress
 * bar and every existing test untouched. Between bar closes this hook runs a
 * requestAnimationFrame loop that paints the *forming* bar straight into the
 * engine via the chart handle. React re-renders stay at the bar cadence; only the
 * canvas moves.
 *
 * The bar being formed is `candles[binarySearchFirstAfter(candles, cursor)]` —
 * literally the bar usePlayback will reveal at the end of the current interval,
 * from the same search function, so the two can never disagree.
 *
 * Everything here is decoration over an unchanged playback loop: with no data,
 * a slow network, a failed fetch or a machine that can't keep up, forming bars
 * simply stop appearing. The cursor never stalls and never stutters.
 */

/** Frame floor, ~30fps: 30 steps/bar at 0.5x, 15 at 1x, 7 at 2x. */
const MIN_FRAME_MS = 33
/** Frame ceiling once the governor backs off. */
const MAX_FRAME_MS = 200
/** Share of each frame the paint may occupy before the governor slows down. */
const PAINT_DUTY = 0.25
const EWMA_ALPHA = 0.2
/** Consecutive over-budget frames before giving up for the session. */
const OVER_BUDGET_STRIKES = 3
/**
 * Fewer steps than this per bar reads as a stutter rather than a build, so the
 * governor gives up instead. Expressed in steps rather than as a fraction of the
 * interval because MAX_FRAME_MS caps the frame gap: a fraction-of-interval rule
 * is simply unreachable at 0.5x and 1x, which would make the governor dead code
 * at exactly the speeds people will use most.
 */
const MIN_USEFUL_STEPS = 3

export interface UseFormingBarOptions {
  /** Master switch — false disables the feature entirely (placeholder, tests). */
  enabled?: boolean
  symbol: string
  timeframe: string
  speed: number
  isPlaying: boolean
  /** The replay cursor, unix seconds. Advances one bar per playback tick. */
  progressViewTs: number
  candlesRef: React.RefObject<CandleData[]>
  maxAvailableTsRef: React.RefObject<number | null>
  chartRef: React.RefObject<BacktestChartHandle | null>
  /** Speeds slow enough for sub-bar animation to be worth showing. */
  tickReplaySpeeds: ReadonlySet<number>
  /**
   * Wall-clock milliseconds per bar, overriding `getPlayTiming(speed).intervalMs` as the
   * animation budget. Mirrors usePlayback's option of the same name — sharing the number is
   * what keeps the candle's build time equal to the bar's dwell time, which is the whole
   * point of reading the timing from there rather than choosing one here.
   */
  barIntervalMsOverride?: number | null
}

export interface UseFormingBarReturn {
  /** Animating right now. */
  active: boolean
  /** Sub-bar data can be had for this symbol/timeframe. */
  available: boolean
  /** How this timeframe is subdivided, for the UI to explain itself. */
  source: SubBarSource
}

interface FillTarget {
  start: number
  end: number
}

export default function useFormingBar({
  enabled = true,
  symbol,
  timeframe,
  speed,
  isPlaying,
  progressViewTs,
  candlesRef,
  maxAvailableTsRef,
  chartRef,
  tickReplaySpeeds,
  barIntervalMsOverride = null,
}: UseFormingBarOptions): UseFormingBarReturn {
  // MARKET-CLOCK pacing — a bar dwelling a whole timeframe of wall clock — is exactly the case
  // the per-bar density ceiling gets wrong. See REALTIME_MAX_TICK_BAR_SECONDS. Judged on the
  // dwell's LENGTH, not on whether an override is set: the walkthrough's slider sets one at every
  // speed, and its 250-1500ms bars belong on the ordinary plan exactly as the Backtest tab's
  // 0.5x-2x do.
  const realtime = isMarketClockDwell(barIntervalMsOverride)
  const preferredSource = useMemo(
    () =>
      subBarSourceFor(
        timeframe,
        realtime ? REALTIME_MAX_TICK_BAR_SECONDS : MAX_TICK_BAR_SECONDS,
      ),
    [timeframe, realtime],
  )

  // Remembered per symbol and DERIVED rather than reset by an effect, so
  // switching instruments clears it for free.
  const [unavailableSymbol, setUnavailableSymbol] = useState<string | null>(null)
  const available = unavailableSymbol !== symbol
  const [governorOff, setGovernorOff] = useState(false)

  /**
   * Symbol+timeframe whose tick supply has been given up on, and downgraded to M1 rows.
   *
   * Market-clock pacing is the only thing that asks for ticks above M6, so it is the only
   * case that can lose an animation it used to have when a deployment turns out to hold no
   * tick archive. Falling back keeps one step per real minute rather than nothing. Not for
   * engine-paced replay, which dwells 250-1000ms on a bar, where a few M1 rows buy nothing.
   */
  const [tickFallbackKey, setTickFallbackKey] = useState<string | null>(null)
  const sourceKey = `${symbol}|${timeframe}`
  const tfSec = getTimeframeSeconds(timeframe)
  // An M1 bar cannot be subdivided by M1 bars, so there is nothing to fall back TO.
  const canFallBack = realtime && tfSec >= 120
  const source: SubBarSource =
    preferredSource === 'ticks' && canFallBack && tickFallbackKey === sourceKey
      ? 'm1'
      : preferredSource
  const supported = source === 'm1' || source === 'ticks'

  const plan = useMemo(
    () => formingBarPlan({ source, tfSec, barIntervalMs: barIntervalMsOverride }),
    [source, tfSec, barIntervalMsOverride],
  )

  // A wall-clock override IS the caller declaring the bar animates (with `enabled` as its say),
  // so it stands in for the speed test: an overridden caller reports one nominal engine speed
  // while the bar's real dwell comes from the override, which is exactly what a
  // speed-membership rule gets wrong.
  const isTickSpeed = barIntervalMsOverride != null || tickReplaySpeeds.has(speed)
  // `armed` is "this detail animates at all"; `running` adds "and the replay is moving".
  // The split is what lets a PAUSE freeze the half-built candle on screen instead of wiping
  // it — pausing does not leave the mode, and a candle that vanishes when you stop the replay
  // reads as the chart jumping back a bar.
  //
  // `active` is what the UI reports. It differs from `running` by `available`, deliberately:
  // gating the loop on availability would deadlock, because the loop is the only thing that
  // would ever discover the source working again.
  const armed = enabled && supported && !governorOff && isTickSpeed
  const running = armed && isPlaying
  const active = running && available

  const planRef = useRef<FormingBarPlan>(plan)
  const bufferRef = useRef<FormingBarBuffer | null>(null)
  const builtGeometryRef = useRef<{ capacity: number; steps: number } | null>(null)
  /**
   * Bumped whenever the bar grid or the ring's shape changes.
   *
   * A reply in flight across that moment was folded for a buffer that no longer exists — its
   * bar boundaries belong to the old grid — so it has to be dropped, exactly as a reply for a
   * previous symbol is.
   */
  const planEpochRef = useRef(0)

  const getBuffer = useCallback((): FormingBarBuffer => {
    const { capacity, steps } = planRef.current
    const built = builtGeometryRef.current
    if (!bufferRef.current || built?.capacity !== capacity || built?.steps !== steps) {
      bufferRef.current = new FormingBarBuffer(capacity, steps)
      builtGeometryRef.current = { capacity, steps }
    }
    return bufferRef.current
  }, [])

  // A second socket, deliberately not the window hook's. The server's message
  // loop is serial per connection, so sharing would queue these small prefetches
  // behind multi-hundred-ms range fetches; and rejectAll on close would let a
  // tick hiccup reject an in-flight candle-window request. Lazy because the
  // constructor throws when DATAFEED_WS is unset.
  const socketRef = useRef<DatafeedSocket | null>(null)
  const getSocket = useCallback((): DatafeedSocket => {
    if (!socketRef.current) socketRef.current = new DatafeedSocket()
    return socketRef.current
  }, [])

  useEffect(() => {
    return () => {
      socketRef.current?.dispose()
      socketRef.current = null
    }
  }, [])

  // Reused across frames so the loop allocates nothing.
  const outRef = useRef<FormingBarState>({
    time: 0,
    open: 0,
    high: 0,
    low: 0,
    close: 0,
    volume: 0,
  })

  const speedRef = useRef(speed)
  const barIntervalMsRef = useRef(barIntervalMsOverride)
  const timeframeRef = useRef(timeframe)
  const symbolRef = useRef(symbol)
  const sourceRef = useRef(source)
  const tfSecRef = useRef(getTimeframeSeconds(timeframe))
  const progressViewTsRef = useRef(progressViewTs)
  const runningRef = useRef(running)
  const inFlightRef = useRef(false)
  const rafRef = useRef<number | null>(null)
  /** performance.now() when the current bar started dwelling. */
  const barStartedAtRef = useRef(0)
  const lastPaintAtRef = useRef(0)
  const paintEwmaRef = useRef(0)
  const minFrameMsRef = useRef(MIN_FRAME_MS)
  const strikesRef = useRef(0)
  /** Bar time the forming bar is currently painted for, or null. */
  const paintedBarRef = useRef<number | null>(null)
  /** Step index the forming bar is currently painted at, or -1. */
  const paintedStepRef = useRef(-1)
  const canFallBackRef = useRef(canFallBack)
  const sourceKeyRef = useRef(sourceKey)
  /** Previous isPlaying, and when the pause began, so a resume can skip the paused time. */
  const wasPlayingRef = useRef(isPlaying)
  const pausedAtRef = useRef<number | null>(null)
  const isPlayingRef = useRef(isPlaying)

  useLayoutEffect(() => {
    speedRef.current = speed
  }, [speed])
  useLayoutEffect(() => {
    barIntervalMsRef.current = barIntervalMsOverride
  }, [barIntervalMsOverride])
  useLayoutEffect(() => {
    timeframeRef.current = timeframe
    tfSecRef.current = getTimeframeSeconds(timeframe)
  }, [timeframe])
  useLayoutEffect(() => {
    symbolRef.current = symbol
  }, [symbol])
  useLayoutEffect(() => {
    sourceRef.current = source
  }, [source])
  useLayoutEffect(() => {
    planRef.current = plan
  }, [plan])
  useLayoutEffect(() => {
    canFallBackRef.current = canFallBack
    sourceKeyRef.current = sourceKey
  }, [canFallBack, sourceKey])
  useLayoutEffect(() => {
    runningRef.current = running
  }, [running])

  // THE GOVERNOR'S VERDICT IS ABOUT A DWELL. It latches off for the mount, which was fine while
  // nothing changed the dwell mid-mode; the walkthrough's slider does, and a user who swept to
  // the fast end (where `enabled` drops) and back has asked a new question. Re-enabling is the
  // moment to answer it afresh, so the strikes and the verdict reset there.
  useLayoutEffect(() => {
    if (!enabled) return
    strikesRef.current = 0
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot reset keyed on the switch
    setGovernorOff(false)
  }, [enabled])

  const clearFormingBar = useCallback(() => {
    paintedStepRef.current = -1
    if (paintedBarRef.current === null) return
    paintedBarRef.current = null
    chartRef.current?.setFormingBar(null)
  }, [chartRef])

  // A pause FREEZES the bar's dwell; it does not restart it. Shifting the start forward by
  // exactly the time spent paused makes `elapsed` skip that time, which is the same bookkeeping
  // usePlayback does when it resumes on the remainder of the bar's wait — so the candle picks
  // up where it stopped and still lands on the reveal. Resetting to now instead would rebuild
  // the candle from its open, and not resetting at all would snap it straight to complete.
  useLayoutEffect(() => {
    isPlayingRef.current = isPlaying
    if (isPlaying === wasPlayingRef.current) return
    const now = performance.now()
    if (isPlaying) {
      if (pausedAtRef.current !== null) barStartedAtRef.current += now - pausedAtRef.current
      pausedAtRef.current = null
    } else {
      pausedAtRef.current = now
    }
    wasPlayingRef.current = isPlaying
  }, [isPlaying])

  // The cursor moving is what starts a new bar's dwell. Moving BACKWARDS (scrub, reset, jump)
  // invalidates the folded bars, since they may belong to a window that is about to be
  // reloaded around a different centre.
  //
  // A cursor that moves while PAUSED — a scrub, a step — also drops the painted bar, whichever
  // way it went: the frozen candle belongs to the bar that was forming before the jump, and
  // with the loop stopped nothing would ever repaint it.
  useLayoutEffect(() => {
    const prev = progressViewTsRef.current
    progressViewTsRef.current = progressViewTs
    barStartedAtRef.current = performance.now()
    if (progressViewTs < prev) {
      bufferRef.current?.clear()
      clearFormingBar()
    } else if (progressViewTs !== prev && !isPlayingRef.current) {
      clearFormingBar()
      // The freeze now dates from this jump, not from the pause that preceded it.
      pausedAtRef.current = performance.now()
    }
  }, [progressViewTs, clearFormingBar])

  // A different ring shape: its cells are the wrong size, so it is REBUILT rather than
  // cleared. The unavailable set goes with it, deliberately — a plan change usually means a
  // different source, and "M1 rows found nothing here" says nothing about ticks.
  useLayoutEffect(() => {
    bufferRef.current = null
    builtGeometryRef.current = null
    planEpochRef.current++
    clearFormingBar()
  }, [plan.capacity, plan.steps, plan.source, clearFormingBar])

  // A new bar grid: everything folded belongs to the old one. Not subsumed by the effect
  // above — H1 to H4 at ordinary pacing keeps the same geometry while changing every boundary.
  useLayoutEffect(() => {
    bufferRef.current?.clear()
    planEpochRef.current++
    clearFormingBar()
  }, [timeframe, clearFormingBar])

  // A different instrument: everything folded belongs to the old one. Its
  // availability needs no reset — that is derived from `symbol` above.
  useLayoutEffect(() => {
    bufferRef.current?.reset()
    clearFormingBar()
  }, [symbol, clearFormingBar])

  // Leaving tick mode (speed up, detail change, unavailable) must not leave a phantom bar
  // behind: it would be hoverable and clickable but absent from getCandleCount(), the
  // click→tooltip bounds check and the legend fallback. Keyed on `armed`, not `running`, so a
  // pause keeps its candle — the mode is still on, and the loop resumes onto the same bar.
  useLayoutEffect(() => {
    if (!armed) clearFormingBar()
  }, [armed, clearFormingBar])

  /**
   * Fold one reply into per-display-bar buckets.
   *
   * Bars beyond `coveredTo` are left untouched — neither folded nor marked — so
   * the next fill picks them up. Folding a partially-served bar would produce a
   * candle that never reaches its real close.
   */
  const distribute = useCallback(
    (targets: FillTarget[], rows: SubBarRow[], coveredTo: number): number => {
      const buffer = getBuffer()
      let r = 0
      let ingested = 0
      for (const { start, end } of targets) {
        if (end > coveredTo) break
        const startMs = start * 1000
        const endMs = end * 1000
        while (r < rows.length && rows[r].timeMs < startMs) r++
        const group: SubBarRow[] = []
        while (r < rows.length && rows[r].timeMs < endMs) group.push(rows[r++])
        // An empty group marks the bar unavailable, so it is never re-requested.
        buffer.ingest(start, end - start, group)
        ingested++
      }
      return ingested
    },
    [getBuffer],
  )

  /** One fill over `[from, to)`, from whichever source this timeframe uses. */
  const fetchFill = useCallback(
    (symbol: string, from: number, to: number): Promise<SubBarFill> => {
      const socket = getSocket()
      if (sourceRef.current === 'ticks') return fetchTicks(socket, symbol, from, to)
      // The M1 path has no availability signal of its own, so an empty reply over
      // a span that definitely holds bars is the only thing to go on.
      return fetchM1SubBars(socket, symbol, from, to).then((rows) => ({
        rows,
        coveredTo: to,
        available: rows.length > 0,
      }))
    },
    [getSocket],
  )

  const requestFill = useCallback(() => {
    if (inFlightRef.current || !runningRef.current) return

    const candles = candlesRef.current ?? []
    const start = binarySearchFirstAfter(candles, progressViewTsRef.current)
    if (start >= candles.length) return

    const buffer = getBuffer()
    const maxTs = maxAvailableTsRef.current
    const tfSec = getTimeframeSeconds(timeframeRef.current)
    // Ticks are orders of magnitude denser per bar than M1 rows, so the tick path
    // reaches less far ahead for a comparable payload.
    const { lookahead, refillBelow, maxBarsPerFill } = planRef.current
    const limit = Math.min(candles.length, start + lookahead)

    // Bar ends come from the next bar's start where possible, so calendar-aligned
    // timeframes (W1, MN1) get their real duration rather than a nominal one.
    const targets: FillTarget[] = []
    let ready = 0
    for (let i = start; i < limit; i++) {
      const barTime = candles[i].time
      if (maxTs !== null && barTime > maxTs) break
      if (buffer.has(barTime)) {
        ready++
        continue
      }
      if (buffer.isUnavailable(barTime)) continue
      const next = i + 1 < candles.length ? candles[i + 1].time : barTime + tfSec
      // A WEEKEND is not a long bar. Friday's last M5 bar has Monday's first as its successor,
      // so the raw rule would call it 51 hours long — asking the server for a span it caps, and
      // handing `ingest` a duration that buckets the bar's real ticks into the first 2% of its
      // steps. Clamped only on the tick path, where every eligible timeframe (M1-H1) has an
      // exact tfSec; the m1 path keeps the raw rule because MN1's real length varies.
      const end =
        planRef.current.source === 'ticks' ? Math.min(next, barTime + tfSec) : next
      targets.push({ start: barTime, end })
    }

    if (targets.length === 0) return
    // Plenty of runway already folded — don't spend a request yet.
    if (ready >= refillBelow) return
    // In the steady state only one bar is ever unfolded; this bounds the FIRST fill after a
    // scrub or a detail change, which would otherwise ask for the whole lookahead at once.
    if (targets.length > maxBarsPerFill) targets.length = maxBarsPerFill

    const from = targets[0].start
    const to = targets[targets.length - 1].end
    const requestSymbol = symbolRef.current
    const requestEpoch = planEpochRef.current

    inFlightRef.current = true
    fetchFill(requestSymbol, from, to)
      .then((fill) => {
        // A window reload, instrument change or geometry change landed while this was in
        // flight; either way the buffer it was folded for is gone.
        if (requestSymbol !== symbolRef.current || requestEpoch !== planEpochRef.current) return
        const ingested = distribute(targets, fill.rows, fill.coveredTo)

        // Two ways the tick path can be useless here, and both are answered by asking a
        // different source rather than by giving up: the deployment holds no tick archive
        // ('none'), or the server's row cap cut the very first bar in half, which would
        // otherwise blacklist one bar per request forever. A market gap is NEITHER — it comes
        // back available with no rows, and a bar with no ticks is simply a flat one.
        const tickPathFailed =
          planRef.current.source === 'ticks' &&
          (!fill.available || (ingested === 0 && fill.coveredTo < targets[0].end))
        if (tickPathFailed && canFallBackRef.current) {
          // Deliberately no markUnavailable: we are about to ask another source for these
          // same bars, and the geometry change rebuilds the buffer anyway.
          setTickFallbackKey(sourceKeyRef.current)
          return
        }

        // A reply that couldn't even cover the first bar would otherwise be
        // re-requested every frame. Give up on that one bar instead of spinning.
        if (ingested === 0) buffer.markUnavailable(targets[0].start)
        setUnavailableSymbol(fill.available ? null : requestSymbol)
      })
      .catch(() => {
        if (requestSymbol !== symbolRef.current || requestEpoch !== planEpochRef.current) return
        // Negative-cache the span so a persistent failure can't spin.
        for (const { start: s } of targets) buffer.markUnavailable(s)
        // Report it, rather than leaving the speed popover claiming the marked
        // speeds animate while nothing does. This covers a datafeed too old to
        // know the `ticks` frame, which rejects with "Unknown message type".
        // A transient failure recovers on the next successful fill.
        setUnavailableSymbol(requestSymbol)
      })
      .finally(() => {
        inFlightRef.current = false
      })
  }, [candlesRef, maxAvailableTsRef, getBuffer, fetchFill, distribute])

  // ---- The animation loop --------------------------------------------------
  // Depends only on `active`, so it is not torn down and rebuilt every bar;
  // everything else is read through refs.
  useEffect(() => {
    if (!running) return

    let cancelled = false

    const frame = () => {
      if (cancelled) return
      rafRef.current = requestAnimationFrame(frame)

      const now = performance.now()
      if (now - lastPaintAtRef.current < minFrameMsRef.current) return

      const candles = candlesRef.current ?? []
      const idx = binarySearchFirstAfter(candles, progressViewTsRef.current)
      if (idx >= candles.length) {
        clearFormingBar()
        return
      }

      const bar = candles[idx]
      const maxTs = maxAvailableTsRef.current
      // Never form a bar the backtest hasn't produced yet.
      if (maxTs !== null && bar.time > maxTs) {
        clearFormingBar()
        return
      }

      const buffer = getBuffer()
      if (!buffer.has(bar.time)) {
        // Nothing folded for this bar: paint nothing and let the reveal handle it.
        clearFormingBar()
        requestFill()
        return
      }

      const steps = buffer.stepCount()
      const override = barIntervalMsRef.current
      const { intervalMs } =
        override != null && override > 0
          ? { intervalMs: override }
          : getPlayTiming(speedRef.current)
      const elapsed = now - barStartedAtRef.current
      const progress = Math.min(1, Math.max(0, elapsed / Math.max(1, intervalMs)))
      let step = Math.min(steps - 1, Math.floor(progress * steps))
      // A BAR NEVER UN-FORMS. Dragging the speed slider slower mid-bar lengthens `intervalMs`, so
      // `progress` drops and the step with it; painting that would shrink the candle on screen.
      // Hold it at what this bar has already shown — the reveal still lands at the new interval.
      if (bar.time === paintedBarRef.current) step = Math.max(step, paintedStepRef.current)

      // Governor verdict, taken on every frame that clears the throttle rather than only on
      // the ones that paint. It reads the MEASURED paint cost against the bar's budget, and a
      // machine that cannot keep up is no less behind on a frame whose candle happened not to
      // move — leaving it after the paint would let the dedupe below hide the very signal it
      // exists to catch.
      //
      // No caller offers a "turn the animation down" control — speed and detail pick whether
      // it runs, not how hard — so backing off here is the sole protection.
      if (intervalMs / minFrameMsRef.current < MIN_USEFUL_STEPS) {
        strikesRef.current++
        if (strikesRef.current >= OVER_BUDGET_STRIKES) {
          if (import.meta.env.DEV) {
            console.warn(
              `[useFormingBar] disabling sub-bar animation: paints average ` +
                `${paintEwmaRef.current.toFixed(1)}ms against a ${intervalMs}ms bar`,
            )
          }
          setGovernorOff(true)
        }
      } else {
        strikesRef.current = 0
      }

      // THE STEP INDEX IS THE CANDLE. Repainting the same index costs an updateData, a
      // visible-range check, a full overlay reconcile and every repaint listener, and moves
      // not one pixel. At a 33ms floor against a real-time H1 bar that is ~109,000 paints for
      // 8,192 states; skipping the repeats makes it one paint per state. requestFill still
      // runs — the prefetcher must not ride on whether the candle happened to move.
      if (bar.time === paintedBarRef.current && step === paintedStepRef.current) {
        requestFill()
        return
      }

      if (!buffer.stateAt(bar.time, step, outRef.current)) return

      // The sub-bar cursor, so a fill part-way through the bar shows its arrow before the bar
      // closes. Quantised to the STEP rather than to raw progress: the body only knows the
      // market up to `step`, so a cursor running ahead of it would reveal an order against a
      // candle that does not yet contain the tick that caused it.
      const barEnd = idx + 1 < candles.length ? candles[idx + 1].time : bar.time + tfSecRef.current
      const revealTs = bar.time + (step / steps) * (barEnd - bar.time)

      const startedAt = performance.now()
      chartRef.current?.setFormingBar(outRef.current, revealTs, step / steps)
      const paintMs = performance.now() - startedAt
      paintedBarRef.current = bar.time
      paintedStepRef.current = step
      lastPaintAtRef.current = now

      // Overlay drawing has no visible-range culling in klinecharts, so a window dense with
      // trade markers can cost several ms per repaint; hold the paint to a fixed share of each
      // frame instead of guessing a fixed rate. Read by the verdict above, on the next frame.
      paintEwmaRef.current =
        paintEwmaRef.current === 0
          ? paintMs
          : paintEwmaRef.current * (1 - EWMA_ALPHA) + paintMs * EWMA_ALPHA
      minFrameMsRef.current = Math.min(
        MAX_FRAME_MS,
        Math.max(MIN_FRAME_MS, Math.round(paintEwmaRef.current / PAINT_DUTY)),
      )

      requestFill()
    }

    rafRef.current = requestAnimationFrame(frame)

    // Stops the loop and nothing else. The painted bar deliberately survives, because the
    // commonest reason this tears down is a PAUSE — and the whole point of the pause is that
    // the half-built candle stays where it is. Every reason the bar should actually go has its
    // own effect: `armed`, the cursor, the timeframe, the symbol, the ring's shape, unmount.
    return () => {
      cancelled = true
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [running, candlesRef, maxAvailableTsRef, chartRef, getBuffer, requestFill, clearFormingBar])

  // Unmount only — `clearFormingBar` is stable, so this cleanup runs once. A bar left painted
  // into an engine this hook no longer drives is the phantom the loop's cleanup used to guard.
  useEffect(() => () => clearFormingBar(), [clearFormingBar])

  // rAF suspends in a hidden tab while playback keeps advancing, so on return the
  // painted bar could belong to a long-past minute. Drop it on the way out.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) clearFormingBar()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [clearFormingBar])

  return { active, available: supported && available && !governorOff, source }
}
